import { getApiClient, publicClient } from '../../api/client';
import type { components, operations } from '../../api/schema';

export type ScorecardTrackingProfile = components['schemas']['ScorecardTrackingProfileRead'];
export type ScorecardTrackingProfilePage = components['schemas']['ScorecardTrackingProfilePage'];
export type ScorecardTrackingProfileMeta = components['schemas']['ScorecardTrackingProfileMeta'];
export type ScorecardTrackingRun = components['schemas']['ScorecardTrackingRunRead'];
export type ScorecardTrackingSnapshot = components['schemas']['ScorecardTrackingSnapshotRead'];
export type ScorecardTrackingMetric = components['schemas']['ScorecardTrackingMetricRead'];
export type ScorecardTrackingPublic = components['schemas']['ScorecardTrackingPublicRead'];
export type ScorecardTrackingSeries = components['schemas']['ScorecardTrackingSeriesRead'];
export type ScorecardRatingProjection = components['schemas']['ScorecardRatingProjectionRead'];
export type CreateScorecardTrackingProfile =
  components['schemas']['ScorecardTrackingProfileCreate'];
export type UpdateScorecardTrackingProfile =
  components['schemas']['ScorecardTrackingProfileUpdate'];
export type RetryScorecardTrackingSources =
  components['schemas']['ScorecardTrackingRetrySourcesCreate'];
export type ScorecardRatingProjectionRequest =
  components['schemas']['ScorecardRatingProjectionRequest'];

type ListTrackingProfilesQuery = NonNullable<
  operations['list_tracking_profiles_scorecard_tracking_profiles_get']['parameters']['query']
>;
type TrackingSeriesQuery = NonNullable<
  operations['get_public_tracking_series_scorecard_tracking_public__slug__series_get']['parameters']['query']
>;

export type ListTrackingProfilesInput = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  ongoingEnabled?: boolean;
  businessType?: string;
  source?: string;
  latestRunStatus?: string;
  updatedAfter?: string;
  updatedBefore?: string;
  includeArchived?: boolean;
  sortBy?: string;
  sortDesc?: boolean;
};

export async function listScorecardTrackingProfiles(
  input: ListTrackingProfilesInput = {},
): Promise<ScorecardTrackingProfilePage> {
  const client = getApiClient();
  const query = {
    page: input.page ?? 1,
    limit: input.limit ?? 20,
    search: input.search || undefined,
    status: input.status || undefined,
    ongoing_enabled: input.ongoingEnabled,
    business_type: input.businessType || undefined,
    source: input.source || undefined,
    latest_run_status: input.latestRunStatus || undefined,
    updated_after: input.updatedAfter,
    updated_before: input.updatedBefore,
    include_archived: input.includeArchived ?? false,
    sort_by: input.sortBy ?? 'updated_at',
    sort_desc: input.sortDesc ?? true,
  } satisfies ListTrackingProfilesQuery;
  const { data, error } = await client.GET('/scorecard-tracking/profiles', {
    params: { query },
  });
  if (error || !data) {
    throw new Error(`Failed to load Scorecard Watchlist: ${JSON.stringify(error)}`);
  }
  return data;
}

export async function getScorecardTrackingProfileMeta(): Promise<ScorecardTrackingProfileMeta> {
  const client = getApiClient();
  const { data, error } = await client.GET('/scorecard-tracking/profiles/meta');
  if (error || !data) {
    throw new Error(`Failed to load Watchlist filters: ${JSON.stringify(error)}`);
  }
  return data;
}

export async function createScorecardTrackingProfile(
  body: CreateScorecardTrackingProfile,
): Promise<ScorecardTrackingProfile> {
  const client = getApiClient();
  const { data, error } = await client.POST('/scorecard-tracking/profiles', { body });
  if (error || !data) {
    throw new Error(`Failed to add business to Watchlist: ${JSON.stringify(error)}`);
  }
  return data;
}

export async function getScorecardTrackingProfile(
  uuid: string,
): Promise<ScorecardTrackingProfile> {
  const client = getApiClient();
  const { data, error } = await client.GET('/scorecard-tracking/profiles/{profile_uuid}', {
    params: { path: { profile_uuid: uuid } },
  });
  if (error || !data) {
    throw new Error(`Failed to load Watchlist business: ${JSON.stringify(error)}`);
  }
  return data;
}

export async function getScorecardTrackingProfileForPlace(
  placeUuid: string,
): Promise<ScorecardTrackingProfile | null> {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/scorecard-tracking/profiles/by-place/{place_uuid}',
    { params: { path: { place_uuid: placeUuid } } },
  );
  if (error) {
    throw new Error(`Failed to load place tracking status: ${JSON.stringify(error)}`);
  }
  return data ?? null;
}

export async function updateScorecardTrackingProfile(
  uuid: string,
  body: UpdateScorecardTrackingProfile,
): Promise<ScorecardTrackingProfile> {
  const client = getApiClient();
  const { data, error } = await client.PATCH('/scorecard-tracking/profiles/{profile_uuid}', {
    params: { path: { profile_uuid: uuid } },
    body,
  });
  if (error || !data) {
    throw new Error(`Failed to update Watchlist business: ${JSON.stringify(error)}`);
  }
  return data;
}

export async function listScorecardTrackingRuns(
  uuid: string,
  limit = 25,
): Promise<ScorecardTrackingRun[]> {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/scorecard-tracking/profiles/{profile_uuid}/runs',
    {
      params: { path: { profile_uuid: uuid }, query: { limit } },
    },
  );
  if (error) {
    throw new Error(`Failed to load tracking runs: ${JSON.stringify(error)}`);
  }
  return data ?? [];
}

export async function getScorecardTrackingRun(
  profileUuid: string,
  runUuid: string,
): Promise<ScorecardTrackingRun> {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/scorecard-tracking/profiles/{profile_uuid}/runs/{run_uuid}',
    { params: { path: { profile_uuid: profileUuid, run_uuid: runUuid } } },
  );
  if (error || !data) {
    throw new Error(`Failed to load tracking run: ${JSON.stringify(error)}`);
  }
  return data;
}

export async function retryScorecardTrackingSources(
  profileUuid: string,
  runUuid: string,
  body: RetryScorecardTrackingSources,
): Promise<ScorecardTrackingRun> {
  const client = getApiClient();
  const { data, error } = await client.POST(
    '/scorecard-tracking/profiles/{profile_uuid}/runs/{run_uuid}/retry-sources',
    { params: { path: { profile_uuid: profileUuid, run_uuid: runUuid } }, body },
  );
  if (error || !data) {
    throw new Error(`Failed to retry tracking sources: ${JSON.stringify(error)}`);
  }
  return data;
}

export async function createScorecardTrackingRun(
  uuid: string,
): Promise<ScorecardTrackingRun> {
  const client = getApiClient();
  const { data, error } = await client.POST(
    '/scorecard-tracking/profiles/{profile_uuid}/runs',
    {
      params: { path: { profile_uuid: uuid } },
      body: { force: false },
    },
  );
  if (error || !data) {
    throw new Error(`Failed to queue tracking run: ${JSON.stringify(error)}`);
  }
  return data;
}

export async function rebuildScorecardTrackingHistory(
  uuid: string,
): Promise<ScorecardTrackingRun> {
  const client = getApiClient();
  const { data, error } = await client.POST(
    '/scorecard-tracking/profiles/{profile_uuid}/rebuild-history',
    { params: { path: { profile_uuid: uuid } } },
  );
  if (error || !data) {
    throw new Error(`Failed to rebuild Scorecard History: ${JSON.stringify(error)}`);
  }
  return data;
}

export async function getPublicScorecardTracking(
  slug: string,
): Promise<ScorecardTrackingPublic> {
  const { data, error } = await publicClient.GET('/scorecard-tracking/public/{slug}', {
    params: { path: { slug } },
  });
  if (error || !data) {
    throw new Error(`Failed to load Scorecard History: ${JSON.stringify(error)}`);
  }
  return data;
}

export type GetTrackingSeriesInput = {
  slug: string;
  metric: string;
  source: string;
  windowDays: number;
  months?: number;
  includeReconstructed?: boolean;
};

export async function getPublicScorecardTrackingSeries(
  input: GetTrackingSeriesInput,
): Promise<ScorecardTrackingSeries> {
  const query = {
    metric: input.metric,
    source: input.source,
    window_days: input.windowDays,
    months: input.months ?? 12,
    include_reconstructed: input.includeReconstructed ?? true,
  } satisfies TrackingSeriesQuery;
  const { data, error } = await publicClient.GET(
    '/scorecard-tracking/public/{slug}/series',
    { params: { path: { slug: input.slug }, query } },
  );
  if (error || !data) {
    throw new Error(`Failed to load score history: ${JSON.stringify(error)}`);
  }
  return data;
}

export async function getPublicScorecardTrackingSnapshot(
  slug: string,
  snapshotUuid: string,
): Promise<ScorecardTrackingSnapshot> {
  const { data, error } = await publicClient.GET(
    '/scorecard-tracking/public/{slug}/snapshots/{snapshot_uuid}',
    { params: { path: { slug, snapshot_uuid: snapshotUuid } } },
  );
  if (error || !data) {
    throw new Error(`Failed to load scorecard snapshot: ${JSON.stringify(error)}`);
  }
  return data;
}

export async function projectPublicScorecardRating(
  slug: string,
  body: ScorecardRatingProjectionRequest,
): Promise<ScorecardRatingProjection> {
  const { data, error } = await publicClient.POST(
    '/scorecard-tracking/public/{slug}/rating-projection',
    { params: { path: { slug } }, body },
  );
  if (error || !data) {
    throw new Error(`Failed to calculate rating target: ${JSON.stringify(error)}`);
  }
  return data;
}
